import { API_PATHS } from './constants';
import {
    IAvailableSlots,
    IVehicleDetails,
    PincodeFromLatitudeLongitudeRequest,
    PincodeFromLatitudeLongitudeResponse,
} from '@/types';
import {
    mapAvailableSlots,
    formatGarageData,
    getObjFromSession,
} from './utils';
import {
    AvailableSlotsDTO,
    BookingDetailsDTO,
    FormattedVehicleDetails,
    CustomerDetails,
    BookingType,
    IAppointmentData,
} from './types';
import {
    getCommonHeaders,
    isClient,
    getUserIdFromCookie,
} from '@/utils/common';
import {
    CAR_ENUMS,
    registrationNumberRegex,
    PERIODIC_SERVICE,
    GARAGE_FRONTEND_SOURCE_ID,
    AUTH_KEYS,
    AUTH_ENTITY_TYPE,
} from '@/constants/common';
import cacheData from 'memory-cache';
import { IncomingMessage } from 'http';
import Cookies from 'js-cookie';
import { NEXTJS_API_ROUTES, API_ROUTES } from '@/constants/apiPaths';
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys';
import { VEHICLE_SOURCE } from '@/constants/constantsAckoDrive';

const getBaseUrl = (): string => {
    if (typeof window !== 'undefined') {
        // If running in the browser, return the current origin
        return window.location.origin;
    }

    return process.env.NEXT_PUBLIC_ENV === 'PRODUCTION'
        ? 'https://www.acko.com'
        : 'https://www.ackodev.com';
};
// export async function checkPolicyDetails(): Promise<any> {
//     const url = NEXTJS_API_ROUTES.POLICY_DETAILS;
//     let userType = '';
//     try {
//         const response: any = await fetch(url, {
//             method: 'GET',
//             credentials: 'include',
//             headers: {
//                 'Content-Type': 'application/json',
//             },
//         });
//         const { result: data } = await response.json();
//         if (data.length > 0) {
//             if (
//                 data.filter((policy: any) => policy.policy_expired).length > 0
//             ) {
//                 userType = 'Lapsed Policy';
//             } else {
//                 userType = 'Active Policy';
//             }
//         }

//         return { userType };
//     } catch (error) {
//         console.error(``);
//     }
// }

export async function checkPolicyDetailsServer(
    req: IncomingMessage
): Promise<any> {
    const url = API_ROUTES.POLICY_DETAILS;
    let userType = '';
    const headers = getCommonHeaders(req.headers);
    try {
        const response: any = await fetch(url, {
            method: 'GET',
            headers,
        });
        const { result: data } = await response.json();
        if (data.length > 0) {
            if (
                data.filter((policy: any) => policy.policy_expired).length > 0
            ) {
                userType = 'Lapsed Policy';
            } else {
                userType = 'Active Policy';
            }
        }

        return { userType };
    } catch (error) {
        console.error(`failed fetching policy`);
        return { userType: 'NA' };
    }
}

const mapPolicyFields = (data: any) => {
    return {
        vehicleDetails: {
            vehicle_make: data?.mmv?.make || '',
            vehicle_model: data?.mmv?.model || '',
            vehicle_name: data?.mmv?.variant || '',
            registration_year: data?.registration_year || '',
            pincode: data?.asset?.insured_details?.pincode || '',
        },
    };
};
export const getAdBaseUrl = () => {
    console.log('debug env', process.env.NEXT_PUBLIC_ENV);
    return process.env.NEXT_PUBLIC_ENV === 'PRODUCTION'
        ? 'https://www.acko.com'
        : 'https://www.ackodev.com';
};

export async function getSavedAssets(): Promise<IVehicleDetails[]> {
    try {
        const res: any = await fetch(NEXTJS_API_ROUTES.GET_SAVED_ASSETS, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!res.ok) {
            throw new Error('Failed to fetch data');
        }
        const data = await res.json();

        // Return the mapped data or an empty array
        return data?.vehicle?.length > 0 ? mapSavedAsset(data) : [];
    } catch (error) {
        console.error('Error fetching saved assets:', error);
        return [];
    }
}

export async function createVehicle(carNumber: string) {
    const body = JSON.stringify({ asset_number: carNumber });
    const url = NEXTJS_API_ROUTES.CREATE_VEHICLE;
    try {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: body,
        });

        // if (!response.ok) {
        //     throw new Error(`HTTP error! Status: ${response.status}`);
        // }

        const data = await response.json();
        console.log(data);
    } catch (error) {
        console.error('Error:', error);
    }
}

const mapSavedAsset = (data: any = []) => {
    return data.vehicle
        .filter(({ vehicle_type }: any) => CAR_ENUMS.includes(vehicle_type))
        .map((vehicle: any) => {
            const {
                make_name,
                model_name,
                fuel_type,
                vehicle_name,
                asset_number,
            } = vehicle;
            return {
                vehicle_make: make_name || '',
                vehicle_model: model_name || '',
                vehicle_name: vehicle_name || '',
                vehicle_number: asset_number || '',
                fuel_type: fuel_type || '',
            };
        });
};
//todo:add garage id to fetch slots by garage
// export async function getSlots(garageId = 'acko'): Promise<IAvailableSlots> {
//     const url = `${process.env.NEXT_PUBLIC_BASE_URL}${API_PATHS.GET_SLOTS}/${garageId}`;

export async function getNewSlots(
    garageId: string,
    slotType: string,
    pincode: string,
    latitude: number,
    longitude: number
): Promise<IAvailableSlots> {
    if (slotType) {
        const url = NEXTJS_API_ROUTES.SLOTS_AVAILABILITY(
            slotType,
            pincode,
            latitude,
            longitude
        );

        const res: any = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                [AUTH_KEYS.X_ORIGIN_ID]: 'ONLINE',
                [AUTH_KEYS.X_WORKSHOP_ID]: garageId,
                [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
            },
        }).then((response) => response.json());
        const data: AvailableSlotsDTO[] = await res;
        return data?.length > 0 ? mapAvailableSlots(data) : [];
    } else {
        throw new Error('Slot type not found');
    }
}

export async function getNewSlotsServerSide(
    garageId: string,
    slotType: string,
    pincode?: string | number,
    req?: IncomingMessage
): Promise<IAvailableSlots> {
    if (!slotType) {
        return [];
    }
    if (!pincode) {
        return [];
    }
    try {
        const pincodeStr = String(pincode);
        //TODO: use the slot-availability API for slots availability if this function is used in the future
        const url = API_ROUTES.GET_SLOTS(slotType, pincodeStr);

        let headers: Record<string, string>;

        if (req) {
            // Server-side: use getCommonHeaders
            const commonHeaders = getCommonHeaders(req.headers);
            headers = {
                ...commonHeaders,
                'x-origin-id': 'ONLINE',
                'x-workshop-id': garageId,
            };
        } else {
            // Client-side: use getUserIdFromCookie
            const userId = getUserIdFromCookie();
            headers = {
                'Content-Type': 'application/json',
                'x-origin-id': 'ONLINE',
                'x-workshop-id': garageId,
            };

            if (userId) {
                headers['x-auth-user-id'] = userId;
            }
        }

        const res = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (!res.ok) {
            console.error('Error fetching slots:', res.status, res.statusText);
            return [];
        }

        const data: AvailableSlotsDTO[] = await res.json();
        return data?.length > 0 ? mapAvailableSlots(data) : [];
    } catch (error) {
        console.error('Error fetching slots:', error);
        return [];
    }
}

export const getBookingDetails = async (
    bookingId: string
): Promise<BookingDetailsDTO> => {
    if (!bookingId) {
        return {};
    }
    const res: any = await fetch(
        `${NEXTJS_API_ROUTES.BOOKING}?booking_id=${bookingId}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    ).then((response) => response.json());
    const data: BookingDetailsDTO = await res;
    return data?.slot_details ? data : {};
};

export const getAppointmentDetails = async (
    appointmnetId: string,
    req?: IncomingMessage
) => {
    if (!appointmnetId) {
        return {};
    }

    let url;
    if (isClient()) {
        url = `${NEXTJS_API_ROUTES.APPOINTMENT}/${appointmnetId}`;
    } else {
        url = `${API_ROUTES.APPOINTMENT}${appointmnetId}`;
    }
    const response: any = await fetch(`${url}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...getCommonHeaders(req?.headers || {}),
            Accept: 'application/json',
            [AUTH_KEYS.X_ENTITY]: AUTH_ENTITY_TYPE.APPOINTMENT,
            [AUTH_KEYS.X_ENTITY_ID]: appointmnetId,
        },
    });

    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('Appointment details response for - ', appointmnetId, data);
    //setting garageId in sessionStorage
    if (typeof window !== 'undefined') {
        sessionStorage.setItem('garageId', data?.garage_id || '');
    }
    return data;
};

export const fetchVehicleDetails = async (
    registration_number: string
): Promise<any> => {
    const url = NEXTJS_API_ROUTES.GET_VEHICLE_DETAILS(registration_number);
    try {
        const response: any = await fetch(`${url}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        }).then((response) => response.json());
        return response;
    } catch (err) {
        return err;
    }
};

export const fetchVehicleDetailsServer = async (
    registration_number: string
): Promise<any> => {
    const url = API_ROUTES.GET_VEHICLE_DETAILS(
        registration_number,
        VEHICLE_SOURCE
    );
    try {
        const response: any = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        }).then((response) => response.json());
        return response;
    } catch (err) {
        console.error('Error fetching vehicle details', err);
        return err;
    }
};

export const fetchPricing = async (
    id: string,
    fuel_type: string
): Promise<any> => {
    const fuelType = fuel_type?.toUpperCase() || '';
    try {
        const response = await fetch(
            (process.env.NEXT_PUBLIC_BASE_URL +
                `${API_PATHS.GET_PRICING}?model_id=${id}&fuel_type=${fuelType}`) as string
        );

        const parsedRes = await response.json();
        return parsedRes;
    } catch (err) {
        return err;
    }
};

export const fetchPackagePricing = async (
    id: number | null,
    fuel_type: string,
    garageId: string = 'acko'
): Promise<any> => {
    const fuelType = fuel_type?.toUpperCase() || '';
    let url;
    if (isClient()) {
        url = NEXTJS_API_ROUTES.PACKAGE_PRICING(garageId, id, fuelType);
    } else {
        url = API_ROUTES.PACKAGE_PRICING(garageId, id, fuelType);
    }
    try {
        const response = await fetch(url);

        const parsedRes = await response.json();
        return parsedRes;
    } catch (err) {
        return err;
    }
};

export const fetchBrandServicePricing = async (
    id: number | null,
    fuel_type: string,
    garageId: string = 'acko'
): Promise<any> => {
    const fuelType = fuel_type?.toUpperCase() || '';
    let url;
    if (isClient()) {
        url = NEXTJS_API_ROUTES.SERVICE_PRICING(garageId, id, fuelType);
    } else {
        url = API_ROUTES.SERVICE_PRICING(garageId, id, fuelType);
    }
    try {
        const response = await fetch(url);

        const parsedRes = await response.json();
        return parsedRes;
    } catch (err) {
        return err;
    }
};

export const fetchGarageByPincode = async (
    id: string,
    fuel_type: string,
    garageId: string
): Promise<any> => {
    const fuelType = fuel_type?.toUpperCase() || '';
    try {
        const response = await fetch(
            process.env.NEXT_PUBLIC_BASE_URL + `/v1/garage/city/bangalore`
        );

        const parsedRes = await response.json();
        return parsedRes;
    } catch (err) {
        return err;
    }
};

interface Location {
    lat: string;
    lon: string;
    main_text: string;
    description: string;
    house_number: string;
    locality: string;
    pincode: string;
}

export const createBooking = async (payload: BookingType): Promise<any> => {
    try {
        const response = await fetch(NEXTJS_API_ROUTES.BOOKING, {
            method: 'POST',
            credentials: 'include',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.ok) {
            const parsedRes = await response.json();
            return parsedRes;
        } else {
            throw new Error('Failed to create booking');
        }
    } catch (err) {
        throw err;
    }
};

export async function createAppointment(
    vehicleDetails: FormattedVehicleDetails,
    userDetails: CustomerDetails,
    appointmentType: BookingType,
    garageId: string
): Promise<any> {
    const pincode = localStorage.getItem('pincode');
    const url = NEXTJS_API_ROUTES.APPOINTMENT;
    const headers = {
        'Content-Type': 'application/json',
        'x-origin-id': 'ONLINE',
        [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
    };
    const serviceType = sessionStorage.getItem('service-type') || '';
    //Todo- Jugad for drop type
    const appointment =
        appointmentType === BookingType.SELF_DROP
            ? BookingType.SLOT_BOOKING
            : appointmentType;
    const search = new URLSearchParams(window.location.search);

    const body = {
        pincode: pincode,
        utm_medium: search.get('utm_medium') || 'ACKO_ONLINE_BOOKING',
        service_type:
            serviceType === PERIODIC_SERVICE
                ? 'PERIODIC_MAINTENANCE'
                : 'YET_TO_BE_DECIDED', //this needs to be changed
        customer_details: userDetails,

        vehicle_details: vehicleDetails,
        booking_details: {
            appointment_fulfillment_type: appointment || 'PICK_AND_DROP',
        },
        pickup_details: {},
        //TO-do: this needs to be dynamic
        garage_id: garageId,
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            credentials: 'include',

            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.text || 'An unknown error occurred';
            throw new Error(`${errorMessage}`);
        }

        const data = await response.json();
        console.log('Appointment created successfully:', data);
        return data;
    } catch (error) {
        console.error('Error creating appointment:', error);
        throw error;
    }
}

export async function updateAppointment(payload: any): Promise<any> {
    const url =
        `${NEXTJS_API_ROUTES.APPOINTMENT}/${payload.appointment_id}` as string;

    const headers = {
        'Content-Type': 'application/json',
        'x-origin-id': 'ONLINE',
        [AUTH_KEYS.X_ENTITY]: AUTH_ENTITY_TYPE.APPOINTMENT,
        [AUTH_KEYS.X_ENTITY_ID]: payload?.appointment_id,
        [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
    };

    try {
        const response = await fetch(url, {
            method: 'PATCH',
            headers: headers,
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.text || 'An unknown error occurred';
            throw new Error(`${errorMessage}`);
        }

        const data = await response.json();
        console.log('Appointment updated successfully:', data);
        return data;
    } catch (error) {
        console.error('Error updating appointment:', error);
        throw error;
    }
}

export async function getPickDropPricing(garage?: string) {
    const url = (process.env.NEXT_PUBLIC_BASE_URL +
        `${API_PATHS.GET_APPPOINTMENT_PRICING}?garage=${garage}`) as string;
    const headers = {
        'Content-Type': 'application/json',
        'x-origin-id': 'ONLINE',
    };

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: headers,
        });

        const result = await response.json();
        console.log('Pricing details fetched successfully:', result);
        return result;
    } catch (error) {
        console.error('Error fetching pricing details:', error);
        throw error;
    }
}

export const fetchCachedGarageData = async (garageId: string) => {
    try {
        //To:Do- Make it dynamic once strapi changes are deployed on dev
        const response = await fetch(
            `${process.env.NEXT_PUBLIC_ACKO_CMS_URL}/api/acko-garages?filters[garageId][$eq]=${garageId}&populate=all`,
            {
                headers: {
                    Authorization: `Bearer ${process.env.NEXT_PUBLIC_CMS_API_TOKEN}`,
                    'Content-Type': 'application/json',
                    'Strapi-Response-Format': 'v4',
                },
            }
        );
        if (!response.ok) {
            throw new Error('Something went wrong');
        }
        const data = await response.json();

        if (Array.isArray(data.data) && data.data.length > 0) {
            const { attributes } = data.data[0];
            const mappedData = formatGarageData(attributes);
            //console.log('mappedData5 ', mappedData);
            return mappedData;
        } else {
            throw new Error('Something went wrong');
        }
        //Mocked data for testing this will be removed
        // const data1 = garageMockData.filter(
        //     (garage) => garage.garageId === garageId
        // );
        // if (data[0]) {
        //     return data[0];
        // } else {
        //     throw new Error('Something went wrong');
        // }
    } catch (error) {
        console.error('Error fetching garage by identifier:', error);
        //throw error;
    }
};

export const fetchGarageByIdentifier = async (garageId: string) => {
    const CACHE_KEY = `garage_data_${garageId}`;
    return getCachedData(CACHE_KEY, () => fetchCachedGarageData(garageId));
};

export const getCachedData = async <T>(
    key: string,
    fetcher: () => Promise<T>,
    cacheDurationMs: number = 10 * 60 * 1000 //20 minutes
): Promise<T> => {
    // Check if data exists in cache
    const cachedData = cacheData.get(key);
    if (cachedData) {
        console.log(`Cache hit for key: ${key}`);
        return cachedData as T;
    }

    // Fetch data and update cache
    console.log(`Cache miss for key: ${key}, fetching...`);
    const data = await fetcher();
    cacheData.put(key, data, cacheDurationMs);

    return data;
};

export async function cancelAppointment(
    payload: any,
    garageId: string
): Promise<any> {
    const headers = {
        'Content-Type': 'application/json',
        'x-origin-id': 'ONLINE',
        'x-workshop-id': garageId,
        [AUTH_KEYS.X_ENTITY_ID]: payload.appointment_id,
        [AUTH_KEYS.X_ENTITY]: AUTH_ENTITY_TYPE.APPOINTMENT,
        [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
    };

    try {
        const response = await fetch(NEXTJS_API_ROUTES.APPOINTMENT, {
            method: 'DELETE',
            headers: headers,
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.text || 'An unknown error occurred';
            throw new Error(`${errorMessage}`);
        }

        return response;
    } catch (error) {
        throw error;
    }
}

export async function checkCarIsServiceable(garageId: string, make: string) {
    const url = NEXTJS_API_ROUTES.GET_CAR_SERVICEABLE(garageId, make);
    const headers = {
        'Content-Type': 'application/json',
        'x-origin-id': 'ONLINE',
        'x-workshop-id': garageId,
    };

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: headers,
        });
        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.text || 'An unknown error occurred';
            throw new Error(`${errorMessage}`);
        }

        const result = await response.json();
        console.log('Car Serviceable', result);
        return result;
    } catch (error) {
        console.error('Error car not serviceable', error);
        throw error;
    }
}

export async function getGarageFromPincode(pincode: string) {
    let url = '';
    if (isClient()) {
        url = `${NEXTJS_API_ROUTES.PINCODE_MAPPING}/${pincode}`;
    } else {
        url = `${API_ROUTES.PINCODE_MAPPING}/${pincode}`;
    }
    try {
        const res = await fetch(url);
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error('Error fetching data');
        }
    } catch (err) {
        console.log('err', err);
        throw new Error('Error fetching data');
    }
}

export async function getGarageDetailsByCity(city: string, isSeo?: boolean) {
    let url = '';
    if (isClient()) {
        url = `${NEXTJS_API_ROUTES.GET_GARAGE_DETAILS_FROM_CITY}/${city}`;
    } else {
        url = `${API_ROUTES.GET_GARAGE_DETAILS_FROM_CITY}/${city}`;
    }
    console.log({ url });
    try {
        const res = await fetch(url);
        if (res.ok) {
            const parsedRes = await res.json();
            console.log({ parsedRes });

            return mappedCityGarageList(parsedRes, isSeo);
        } else {
            throw new Error('Error fetching garage data');
        }
    } catch (err) {
        throw new Error('err');
    }
}
const mappedCityGarageList = (parsedRes: any, isSeo?: boolean) => {
    return parsedRes.map((garage: any) => ({
        garageName: garage?.name || '',
        garageAddress: garage?.address || '',
        garageRating: garage?.rating || 4, //this needs to be added in the response
        city: garage?.city,
        pincode: garage?.pincode || 560002,
        garageId: garage.id,
        ...(isSeo && {
            garageDisplayAddress: garage?.display_address,
            garageMapLink: garage?.map_link,
            garageLandingPath: garage?.garage_landing_path,
        }),
    }));
};

export async function getGarageDetailsById(garageId: string) {
    let url;

    if (isClient()) {
        url = `${NEXTJS_API_ROUTES.GET_GARAGE_DETAILS_FROM_ID}/${garageId}`;
    } else {
        url = `${API_ROUTES.GET_GARAGE_DETAILS}/${garageId}`;
    }
    console.log({ url });
    try {
        const res = await fetch(url);
        console.log({ res });
        if (res.ok) {
            const parsedRes = await res.json();
            return parsedRes;
        } else {
            throw new Error('Error fetching garage data');
        }
    } catch (err) {
        console.error(err);
    }
}

export async function getPincodeFromUserId() {
    const url = NEXTJS_API_ROUTES.GET_PINCODE_FROM_USERID;
    try {
        const res = await fetch(url);
        if (res.ok) {
            const parsedRes = await res.json();
            console.log({ parsedRes });

            return parsedRes;
        }
        throw new Error('API returned non 200 response');
    } catch (err) {
        throw new Error('API failed');
    }
}

//Revamped flow API calls

export async function createAppointmentV2(
    vehicleDetails: FormattedVehicleDetails,
    userDetails: CustomerDetails,
    appointmentType: BookingType,
    garageId: string
): Promise<any> {
    const isWebView = Boolean(Cookies.get('webview'));
    const pincode = localStorage.getItem('pincode');
    const url = NEXTJS_API_ROUTES.APPOINTMENT;
    const headers = {
        'Content-Type': 'application/json',
        'x-origin-id': 'ONLINE',
        [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
    };
    const serviceType = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.SERVICE_TYPE
    );
    //Todo- Jugad for drop type
    const appointment =
        appointmentType === BookingType.SELF_DROP
            ? BookingType.SELF_DROP
            : appointmentType;
    const search = new URLSearchParams(window.location.search);

    const body = {
        pincode: pincode,
        source: search.get('utm_medium') || 'ACKO_ONLINE_BOOKING',
        is_app: isWebView,
        service_type: serviceType?.toUpperCase() || '',
        customer_details: userDetails,

        vehicle_details: vehicleDetails,
        booking_details: {
            appointment_fulfillment_type: appointment || 'PICK_AND_DROP',
        },
        pickup_details: {},
        //TO-do: this needs to be dynamic
        garage_id: garageId,
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            credentials: 'include',

            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.text || 'An unknown error occurred';
            throw new Error(`${errorMessage}`);
        }

        const data = await response.json();
        console.log('Appointment created successfully:', data);
        return data;
    } catch (error) {
        console.error('Error creating appointment:', error);
        throw error;
    }
}

export const updateAppointmentAPI = async (
    appointmentData: IAppointmentData
): Promise<any> => {
    const url = `${NEXTJS_API_ROUTES.APPOINTMENT}/${appointmentData.appointment_id}`;
    const isWebView = Boolean(Cookies.get('webview'));

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append('x-origin-id', 'ONLINE');
    myHeaders.append(AUTH_KEYS.X_ENTITY_ID, appointmentData.appointment_id);
    myHeaders.append(AUTH_KEYS.X_ENTITY, AUTH_ENTITY_TYPE.APPOINTMENT);
    myHeaders.append(AUTH_KEYS.X_SOURCE_ID, GARAGE_FRONTEND_SOURCE_ID);

    const raw = JSON.stringify({ ...appointmentData, is_app: isWebView });

    const requestOptions: RequestInit = {
        method: 'PATCH',
        headers: myHeaders,
        body: raw,
    };

    const response = await fetch(url, requestOptions);

    if (!response.ok) {
        const errorDetails = await response.json();
        throw new Error(JSON.stringify(errorDetails));
    }

    return response.json();
};

export const fetchAppointmentAPI = async (
    appointmentId: string
): Promise<IAppointmentData> => {
    const url = `${NEXTJS_API_ROUTES.APPOINTMENT}/${appointmentId}`;

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');
    myHeaders.append('x-origin-id', 'ONLINE');
    myHeaders.append(AUTH_KEYS.X_ENTITY_ID, appointmentId);
    myHeaders.append(AUTH_KEYS.X_ENTITY, AUTH_ENTITY_TYPE.APPOINTMENT);
    myHeaders.append(AUTH_KEYS.X_SOURCE_ID, GARAGE_FRONTEND_SOURCE_ID);

    const requestOptions: RequestInit = {
        method: 'GET',
        headers: myHeaders,
    };

    const response = await fetch(url, requestOptions);

    if (!response.ok) {
        const errorDetails = await response.json();
        throw new Error(JSON.stringify(errorDetails));
    }

    return response.json();
};

export const getRegexData = async () => {
    try {
        const res: any = await fetch(
            `${window.location.origin}${process.env.NEXT_PUBLIC_ENV_PREFIX}/api/regex-service`
        );
        const data = await res.json();
        return data.data.registration_number;
    } catch (e) {
        return registrationNumberRegex;
    }
};

export const getUserAndPolicyDetails = async () => {
    try {
        const res: any = await fetch(
            `${window.location.origin}${process.env.NEXT_PUBLIC_ENV_PREFIX}/api/get-user-details`,
            {
                credentials: 'include',
            }
        );
        if (!res.ok) {
            throw new Error('Failed to fetch data');
        }
        const data = await res.json();
        return data;
    } catch (e) {
        console.error(e);
    }
};

export const fetchVehicleInfo = async (id: number | null): Promise<any> => {
    const url = NEXTJS_API_ROUTES.GET_VEHICLE_INFO(id);
    try {
        const res = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (res.ok) {
            const parsedRes = await res.text();

            console.log({ parsedRes });
            return parsedRes;
        } else {
            return '';
        }
    } catch (err) {
        return err;
    }
};

export async function getReviewsCity(garageId: string) {
    const url = (process.env.NEXT_PUBLIC_BASE_URL +
        `/v1/garage/reviews`) as string;
    try {
        const res = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'x-workshop-id': garageId,
            },
        });
        if (res.ok) {
            const parsedRes = await res.json();
            console.log({ parsedRes });
            return parsedRes;
        } else {
            throw new Error('Error fetching garage data');
        }
    } catch (err) {
        console.error('error fetching reviews', err);
    }
}

export interface OrderNPSPayload {
    email: string;
    phone: string;
}

export async function getOrderNPSLink(payload: OrderNPSPayload): Promise<any> {
    const url = NEXTJS_API_ROUTES.CREATE_TYPEFORM_LINK;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        return response;
    } catch (error) {
        throw error;
    }
}

export async function getPincodeFromLatitudeLongitude(
    payload: PincodeFromLatitudeLongitudeRequest
) {
    const url = NEXTJS_API_ROUTES.GET_PINCODE_FROM_LATITUDE_LONGITUDE;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                credentials: 'include',
            },
            body: JSON.stringify(payload),
        });
        if (response.ok) {
            const parsedRes = await response.json();
            return parsedRes as PincodeFromLatitudeLongitudeResponse;
        }
        return null;
    } catch (err) {
        console.error('error fetching pincode from latitude longitude', err);
        return null;
    }
}
